/**
 * Classrooms (ClassroomsController.cs / ClassroomPanelsController.cs, web):
 *  - ClassroomPage (teacher): name, join code, the students who joined (each
 *    with its projects, ItemStudentList + LoadProjects, and the app's actions:
 *    rename, change classroom, delete), the projects shared with the class
 *    (picked from the teacher's own projects), per quiz the classroom's own
 *    ranking / statistics, and per game each student's results.
 *  - StudentView (`#/student`, no account): join with the code and a name,
 *    then play the classroom's projects; quiz answers are tagged with the
 *    classroom and student so the teacher sees them.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import type { Project } from "@makeit/schema";
import type { ProjectSource } from "../data/loadProject";
import { libraryUrl } from "../data/media";
import {
  classroomProjectIds,
  deleteClassroom,
  fetchProjectSummary,
  projectsForStudent,
  setClassroomSharing,
  sharingOf,
  joinClassroom,
  moveStudent,
  refreshStudent,
  removeStudent,
  renameStudent,
  renameClassroom,
  setClassroomProjects,
  watchClassroom,
  watchProject,
  watchStudents,
  type Classroom,
  type JoinedClassroom,
  type ProjectSummary,
  type Student,
} from "../data/myProjects";
import { loadStudent, saveStudent } from "../data/student";
import { asClassCode } from "../data/classCode";
import { t } from "../player/i18n";
import { ConfirmDialog, Icon, Modal, MoreMenu, PromptDialog, SearchBox, TypeBadge, isQuizType, matchesSearch, templateArt } from "./widgets";
import { navigate, useRoute } from "./router";
import { GameResultsReport, RankingReport, StatsReport } from "./Reports";
import { ProjectCardPicker } from "./ProjectCardPicker";

// ---------------------------------------------------------------------------
// Teacher
// ---------------------------------------------------------------------------

export function ClassroomPage({ classroomId, myProjects, classrooms }: { classroomId: string; myProjects: ProjectSummary[]; classrooms: Classroom[] }) {
  const [room, setRoom] = useState<Classroom | null | undefined>(undefined);
  const [students, setStudents] = useState<Student[]>([]);
  const [dialog, setDialog] = useState<"rename" | "delete" | "projects" | null>(null);
  const [report, setReport] = useState<{ projectId: string; tab: "ranking" | "stats" | "games" } | null>(null);
  const [sharing, setSharing] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Student | null>(null);
  const [openStudent, setOpenStudent] = useState<string | null>(null);
  const [studentDialog, setStudentDialog] = useState<null | { kind: "rename" | "move"; student: Student }>(null);

  useEffect(() => {
    let offRoom: (() => void) | undefined;
    let offStudents: (() => void) | undefined;
    let gone = false;
    setRoom(undefined);
    void watchClassroom(classroomId, (c) => !gone && setRoom(c)).then((f) => (gone ? f() : (offRoom = f)));
    void watchStudents(classroomId, (l) => !gone && setStudents(l)).then((f) => (gone ? f() : (offStudents = f)));
    return () => {
      gone = true;
      offRoom?.();
      offStudents?.();
    };
  }, [classroomId]);

  if (room === undefined) return <section className="view" />;
  if (room === null) return <section className="view"><div className="empty muted">{t("errNotFound")}</div></section>;

  const myIds = new Set(myProjects.map((p) => p.id));
  const projects = classroomProjectIds(room)
    .map((id) => myProjects.find((p) => p.id === id))
    .filter((p): p is ProjectSummary => !!p)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <section className="view">
      <header className="view-head">
        <h1>
          <Icon name="class" className="h-icon" /> {room.name}
        </h1>
        <div className="view-tools">
          <MoreMenu
            items={[
              { label: t("rename"), icon: "rename", onClick: () => setDialog("rename") },
              { label: t("delete"), icon: "delete", danger: true, onClick: () => setDialog("delete") },
            ]}
          />
        </div>
      </header>

      <div className="class-code-box">
        <div>
          <p className="muted">{t("classCodeInfo")}</p>
          <div className="share-code">{room.code}</div>
          <p className="muted small">{t("classJoinInfo")}</p>
          <p className="muted small">
            {location.origin}
            {location.pathname}#/student
          </p>
        </div>
      </div>

      {/* students first, two per row (ClassroomPanelsController › studentsPanel) */}
      <div className="class-section">
        <h2>
          {t("students")} <span className="muted">{students.length}</span>
        </h2>
        {students.length === 0 ? (
          <p className="muted">{t("classNoStudents")}</p>
        ) : (
          <div className="student-grid">
            {students.map((s) => {
              const n = projectsForStudent(room, s.id).filter((id) => myIds.has(id)).length;
              return (
                <div key={s.id} className="student-card">
                  <button className="student-main" onClick={() => setOpenStudent(s.id)}>
                    <Icon name="account" />
                    <span className="student-text">
                      <span className="student-name" dir="auto">
                        {s.name}
                      </span>
                      <span className="muted small">{n === 1 ? t("projectsCountOne") : t("projectsCount").replace("{n}", String(n))}</span>
                    </span>
                  </button>
                  <MoreMenu
                    items={[
                      { label: t("projects"), icon: "projects", onClick: () => setOpenStudent(s.id) },
                      { label: t("renameStudent"), icon: "rename", onClick: () => setStudentDialog({ kind: "rename", student: s }) },
                      { label: t("moveStudent"), icon: "class", onClick: () => setStudentDialog({ kind: "move", student: s }) },
                      { label: t("deleteStudentMenu"), icon: "delete", danger: true, onClick: () => setRemoving(s) },
                    ]}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="class-section">
        <header className="view-head">
          <h2>{t("projects")}</h2>
          <button className="btn btn-small" onClick={() => setDialog("projects")}>
            {t("chooseProjects")}
          </button>
        </header>
        {projects.length === 0 ? (
          <p className="muted">{t("classNoProjects")}</p>
        ) : (
          <div className="class-projects">
            {projects.map((p) => (
              <div key={p.id} className="class-project">
                <a className="pcard" href={`#/project/${p.id}`}>
                  <span className="pcard-art" style={templateArt(p.template)}>
                    <TypeBadge type={p.type} />
                  </span>
                  <span className="pcard-name">{p.name}</span>
                </a>
                <button className="class-share-state" onClick={() => setSharing(p.id)}>
                  <Icon name="share" /> <ShareState room={room} projectId={p.id} />
                </button>
                {!isQuizType(p.type) && (
                  <div className="class-project-tools">
                    <button className="link-btn" onClick={() => setReport({ projectId: p.id, tab: "games" })}>
                      {t("gameResults")}
                    </button>
                  </div>
                )}
                {isQuizType(p.type) && (
                  <div className="class-project-tools">
                    <button className="link-btn" onClick={() => setReport({ projectId: p.id, tab: "ranking" })}>
                      {t("ranking")}
                    </button>
                    <button className="link-btn" onClick={() => setReport({ projectId: p.id, tab: "stats" })}>
                      {t("statistics")}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {removing && (
        // AlertsController.ConfirmDeleteStudent
        <ConfirmDialog
          title={`${t("delete")} "${removing.name}"`}
          text={t("deleteStudentText")}
          confirmLabel={t("classDeleteStudent")}
          onConfirm={async () => void (await removeStudent(classroomId, removing.id))}
          onClose={() => setRemoving(null)}
        />
      )}
      {openStudent && students.some((x) => x.id === openStudent) && (
        <StudentProjects room={room} students={students} student={students.find((x) => x.id === openStudent)!} projects={myProjects} onClose={() => setOpenStudent(null)} />
      )}
      {studentDialog?.kind === "rename" && (
        <PromptDialog
          title={t("renameStudent")}
          label={t("studentName")}
          initial={studentDialog.student.name}
          onSubmit={async (name) => void (await renameStudent(classroomId, studentDialog.student.id, name))}
          onClose={() => setStudentDialog(null)}
        />
      )}
      {studentDialog?.kind === "move" && (
        <MoveStudentDialog
          student={studentDialog.student}
          targets={classrooms.filter((c) => c.id !== classroomId).sort((a, b) => a.name.localeCompare(b.name))}
          onMove={(to) => moveStudent(classroomId, studentDialog.student.id, to)}
          onClose={() => setStudentDialog(null)}
        />
      )}
      {dialog === "rename" && <PromptDialog title={t("rename")} label={t("classroomName")} initial={room.name} onSubmit={async (name) => void (await renameClassroom(classroomId, name))} onClose={() => setDialog(null)} />}
      {dialog === "delete" && (
        <ConfirmDialog
          title={`${t("delete")} "${room.name}"`}
          text={`${t("deleteClassroom")}\n${t("deleteClassroomText")}`}
          confirmLabel={t("delete")}
          danger
          onConfirm={async () => {
            await deleteClassroom(classroomId);
            navigate({ name: "projects", filter: "all" });
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "projects" && (
        <ProjectCardPicker
          title={t("chooseProjects")}
          projects={[...myProjects].sort((a, b) => a.name.localeCompare(b.name))}
          initial={classroomProjectIds(room)}
          onDone={(ids) => setClassroomProjects(classroomId, ids)}
          onClose={() => setDialog(null)}
        />
      )}
      {sharing && (
        <Modal title={`${t("share")} · ${myProjects.find((p) => p.id === sharing)?.name ?? ""}`} onClose={() => setSharing(null)} className="modal-wide">
          <ClassroomShareEditor room={room} students={students} projectId={sharing} />
        </Modal>
      )}
      {report && <ClassReportDialog projectId={report.projectId} classroomId={classroomId} tab={report.tab} students={students} onClose={() => setReport(null)} />}
    </section>
  );
}

/** "With all students" / "With 3 students". */
function ShareState({ room, projectId }: { room: Classroom; projectId: string }) {
  const sh = sharingOf(room, projectId);
  const n = sh.students.length;
  return <span>{sh.all ? t("sharedWithAll") : n === 1 ? t("sharedWithOne") : t("sharedWithSome").replace("{n}", String(n))}</span>;
}

/**
 * One student's projects (ClassroomPanelsController.LoadProjects): every
 * project of the teacher with a switch, on when the student has it (shared
 * with the whole class or with this student). Turning on shares it with this
 * student too; turning off a project shared with everybody keeps it for all
 * the other current students.
 */
function StudentProjects({ room, students, student, projects, onClose }: { room: Classroom; students: Student[]; student: Student; projects: ProjectSummary[]; onClose: () => void }) {
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  // the classroom snapshot brings the saved values back
  const savedKey = JSON.stringify([room.projects, room.studentProjects]);
  useEffect(() => setPending({}), [savedKey]);
  const list = [...projects].sort((a, b) => a.name.localeCompare(b.name)).filter((p) => matchesSearch(p.name, q));
  const toggle = (projectId: string) => {
    const sh = sharingOf(room, projectId);
    const has = sh.all || sh.students.includes(student.id);
    const next = sh.all ? students.map((x) => x.id).filter((x) => x !== student.id) : has ? sh.students.filter((x) => x !== student.id) : [...sh.students, student.id];
    setError(false);
    setPending((m) => ({ ...m, [projectId]: !has }));
    setClassroomSharing(room.id, projectId, false, next).catch(() => {
      setPending((m) => {
        const n = { ...m };
        delete n[projectId];
        return n;
      });
      setError(true);
    });
  };
  return (
    <Modal title={student.name} onClose={onClose} className="modal-wide">
      <p className="muted small">{t("studentProjectsInfo")}</p>
      {projects.length > 12 && <SearchBox value={q} onChange={setQ} />}
      {error && <p className="error">{t("errSave")}</p>}
      <ul className="student-projects">
        {list.map((p) => {
          const sh = sharingOf(room, p.id);
          const on = pending[p.id] ?? (sh.all || sh.students.includes(student.id));
          return (
            <li key={p.id}>
              <label>
                <Icon name={isQuizType(p.type) ? "quizzes" : "games"} />
                <span className="sp-name">
                  <span dir="auto">{p.name}</span>
                  {sh.all && <span className="muted small">{t("sharedWithAll")}</span>}
                </span>
                <input type="checkbox" className="switch" checked={on} disabled={p.id in pending} onChange={() => toggle(p.id)} />
              </label>
            </li>
          );
        })}
      </ul>
      {list.length === 0 && <p className="muted">{q.trim() ? t("noSearchResults") : t("noProjectsTitle")}</p>}
    </Modal>
  );
}

/** "Change classroom" (ClassroomsController.MoveStudent): the teacher's other classrooms. */
function MoveStudentDialog({ student, targets, onMove, onClose }: { student: Student; targets: Classroom[]; onMove: (classroomId: string) => Promise<unknown>; onClose: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const pick = async (id: string) => {
    if (busy) return;
    setBusy(id);
    setError(false);
    try {
      await onMove(id);
      onClose();
    } catch {
      setError(true);
      setBusy(null);
    }
  };
  return (
    <Modal title={t("moveStudent")} onClose={onClose}>
      <p className="muted">{t("moveStudentTo").replace("{name}", student.name)}</p>
      {targets.length === 0 ? (
        <p className="muted">{t("noOtherClassrooms")}</p>
      ) : (
        <ul className="pick-list">
          {targets.map((c) => (
            <li key={c.id}>
              <button onClick={() => void pick(c.id)} disabled={!!busy}>
                <Icon name="class" /> {c.name} {busy === c.id && "…"}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="error">{t("errSave")}</p>}
    </Modal>
  );
}

/**
 * How one project is shared in one classroom (ProjectClassroomsShare /
 * SharedUsersPrefab): "Automatically share with all students, including new
 * students", or one tick per student. Every change is saved at once.
 */
export function ClassroomShareEditor({ room, students, projectId }: { room: Classroom; students: Student[]; projectId: string }) {
  const saved = sharingOf(room, projectId);
  const savedKey = `${saved.all}|${[...saved.students].sort().join(",")}`;
  const [pending, setPending] = useState<{ all: boolean; students: string[] } | null>(null);
  const [error, setError] = useState(false);
  // the classroom snapshot brings the saved value back
  useEffect(() => setPending(null), [savedKey]);
  const view = pending ?? saved;
  const apply = (all: boolean, list: string[]) => {
    setError(false);
    setPending({ all, students: list });
    setClassroomSharing(room.id, projectId, all, list).catch(() => {
      setPending(null);
      setError(true);
    });
  };
  const sorted = [...students].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="share-editor">
      <label className="share-all">
        <span>{t("classShareWithAll")}</span>
        {/* turning it off keeps every current student ticked */}
        <input type="checkbox" className="switch" checked={view.all} onChange={() => apply(!view.all, view.all ? students.map((x) => x.id) : [])} />
      </label>
      {!view.all &&
        (sorted.length === 0 ? (
          <p className="muted">{t("classNoStudents")}</p>
        ) : (
          <ul className="share-students">
            {sorted.map((st) => {
              const on = view.students.includes(st.id);
              return (
                <li key={st.id}>
                  <label>
                    <input type="checkbox" checked={on} onChange={() => apply(false, on ? view.students.filter((x) => x !== st.id) : [...view.students, st.id])} />
                    <span dir="auto">{st.name}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        ))}
      {pending && <p className="muted small">…</p>}
      {error && <p className="error">{t("errSave")}</p>}
    </div>
  );
}

/**
 * The project page's "Classrooms" tab (HomeProjectPanel.OpenClassrooms →
 * ProjectClassroomsShare): pick one of the teacher's classrooms, choose who in
 * it gets the project, and see that classroom's results for it.
 */
export function ProjectClassrooms({ project, classrooms }: { project: Project; classrooms: Classroom[] }) {
  const [current, setCurrent] = useState(classrooms[0]?.id ?? "");
  const room = classrooms.find((c) => c.id === current) ?? classrooms[0];
  const [students, setStudents] = useState<Student[] | null>(null);
  useEffect(() => {
    if (!room) return;
    let off: (() => void) | undefined;
    let gone = false;
    setStudents(null);
    void watchStudents(room.id, (l) => !gone && setStudents(l)).then((f) => (gone ? f() : (off = f)));
    return () => {
      gone = true;
      off?.();
    };
  }, [room?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!room) return null;
  const shared = classroomProjectIds(room).includes(project.id);
  return (
    <div className="project-classrooms">
      <div className="chips">
        {classrooms.map((c) => (
          <button key={c.id} className={`chip ${c.id === room.id ? "on" : ""}`} onClick={() => setCurrent(c.id)}>
            <Icon name="class" /> {c.name}
          </button>
        ))}
      </div>
      {students === null ? (
        <div className="empty muted">…</div>
      ) : (
        <>
          <ClassroomShareEditor room={room} students={students} projectId={project.id} />
          {shared && students.length > 0 && (
            <div className="class-section">
              <h2>{isQuizType(project.type) ? t("ranking") : t("gameResults")}</h2>
              {isQuizType(project.type) ? <RankingReport project={project} classroomId={room.id} /> : <GameResultsReport projectId={project.id} classroomId={room.id} students={students} />}
            </div>
          )}
          <p>
            <a className="link-btn" href={`#/classroom/${room.id}`}>
              {t("openClassroom")}
            </a>
          </p>
        </>
      )}
    </div>
  );
}

function ClassReportDialog({ projectId, classroomId, tab, students, onClose }: { projectId: string; classroomId: string; tab: "ranking" | "stats" | "games"; students: Student[]; onClose: () => void }) {
  const [project, setProject] = useState<Project | null>(null);
  useEffect(() => {
    let off: (() => void) | undefined;
    let gone = false;
    void watchProject(projectId, (p) => !gone && setProject(p)).then((f) => (gone ? f() : (off = f)));
    return () => {
      gone = true;
      off?.();
    };
  }, [projectId]);
  return (
    <Modal title={project ? `${project.name} · ${tab === "ranking" ? t("ranking") : tab === "stats" ? t("statistics") : t("gameResults")}` : "…"} onClose={onClose} className="modal-wide">
      {project && tab === "ranking" && <RankingReport project={project} classroomId={classroomId} />}
      {project && tab === "stats" && <StatsReport project={project} classroomId={classroomId} />}
      {project && tab === "games" && <GameResultsReport projectId={projectId} classroomId={classroomId} students={students} />}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Student
// ---------------------------------------------------------------------------

export function StudentView({ onPlay }: { onPlay: (source: ProjectSource) => void }) {
  const route = useRoute();
  const [joined, setJoined] = useState<JoinedClassroom | null>(() => loadStudent());
  const [room, setRoom] = useState<Classroom | null | undefined>(undefined);
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [leave, setLeave] = useState(false);
  const [gone, setGone] = useState(false);

  // Where this device's student stands now: moved to another classroom, renamed, or removed.
  const joinedKey = joined ? `${joined.classroomId}/${joined.studentId}` : "";
  useEffect(() => {
    if (!joined) return;
    let stop = false;
    void refreshStudent(joined).then((r) => {
      if (stop || r === null) return;
      if (r === "gone") return setGone(true);
      if (r.classroomId !== joined.classroomId || r.name !== joined.name || r.classroomName !== joined.classroomName) {
        saveStudent(r);
        setJoined(r);
      }
    });
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinedKey]);

  useEffect(() => {
    if (!joined) return;
    let off: (() => void) | undefined;
    let gone = false;
    void watchClassroom(joined.classroomId, (c) => !gone && setRoom(c)).then((f) => (gone ? f() : (off = f)));
    return () => {
      gone = true;
      off?.();
    };
  }, [joined]);

  // only the projects shared with everybody or with this student
  const mine = useMemo(() => (room && joined ? projectsForStudent(room, joined.studentId) : []), [room, joined]);
  const ids = mine.join(",");
  useEffect(() => {
    if (!room) return;
    let gone = false;
    setProjects(null);
    Promise.all(mine.map((id) => fetchProjectSummary(id).catch(() => null))).then((list) => {
      if (!gone) setProjects(list.filter((p): p is ProjectSummary => !!p));
    });
    return () => {
      gone = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  if (joined && gone) {
    return (
      <section className="view student-view">
        <div className="empty">
          <p>{t("studentRemoved")}</p>
          <button
            className="btn"
            onClick={() => {
              saveStudent(null);
              setGone(false);
              setRoom(undefined);
              setJoined(null);
            }}
          >
            {t("classJoin")}
          </button>
        </div>
      </section>
    );
  }

  if (!joined) {
    return (
      <section className="view student-view">
        <a className="back" href="#/">
          ‹ Make It
        </a>
        <JoinForm code={route.name === "student" ? route.code : undefined} onJoined={(j) => {
          saveStudent(j);
          setJoined(j);
        }} />
      </section>
    );
  }

  return (
    <section className="view student-view">
      <header className="view-head">
        <h1>
          <Icon name="class" className="h-icon" /> {room?.name ?? joined.classroomName}
        </h1>
        <button className="link-btn" onClick={() => setLeave(true)}>
          {t("classLeave")}
        </button>
      </header>
      <p className="muted">
        {t("classStudentNameInfo")} <strong dir="auto">{joined.name}</strong>
      </p>
      {room === null && <div className="empty muted">{t("classErrorNotExist")}</div>}
      {room && projects === null && <div className="empty muted">…</div>}
      {room && projects && projects.length === 0 && <div className="empty muted">{t("classNoProjectsStudent")}</div>}
      {projects && projects.length > 0 && (
        <div className="pgrid">
          {projects.map((p) => (
            <button key={p.id} className="pcard" onClick={() => onPlay({ kind: "project", projectId: p.id })}>
              <span className="pcard-art" style={templateArt(p.template)}>
                <TypeBadge type={p.type} />
              </span>
              <span className="pcard-name">{p.name}</span>
            </button>
          ))}
        </div>
      )}
      {leave && (
        // AlertsController.ConfirmLeaveClassroom
        <ConfirmDialog
          title={room?.name || joined.classroomName || t("classLeave")}
          text={t("classLeaveConfirm")}
          confirmLabel={t("classLeave")}
          danger
          onConfirm={() => {
            saveStudent(null);
            setJoined(null);
            setRoom(undefined);
          }}
          onClose={() => setLeave(false)}
        />
      )}
    </section>
  );
}

/** `code`: the classroom code typed elsewhere (the code box of the home page, #/student/<code>). */
function JoinForm({ code: initialCode, onJoined }: { code?: string; onJoined: (j: JoinedClassroom) => void }) {
  const [code, setCode] = useState(() => (initialCode ? asClassCode(initialCode) ?? "" : ""));
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      onJoined(await joinClassroom(asClassCode(code) ?? code, name.trim()));
    } catch (err) {
      const msg = String((err as Error).message ?? "");
      setError(msg.includes("no-classroom") || msg.includes("not-found") ? t("classErrorNotExist") : t("errGeneric"));
      setBusy(false);
    }
  };
  return (
    <form className="join-form" onSubmit={submit}>
      <img src={libraryUrl("ui/redeem.png")} alt="" className="join-art" />
      <h1>{t("classJoin")}</h1>
      <p className="muted">{t("classNoText")}</p>
      <input dir="auto" className="text-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("classNameStudent")} maxLength={40} required aria-label={t("classNameStudent")} />
      <input className="text-input code-like" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))} placeholder={t("classCode")} autoCapitalize="characters" autoComplete="off" autoCorrect="off" spellCheck={false} required aria-label={t("classCode")} />
      {error && <p className="error">{error}</p>}
      <button className="btn btn-big" type="submit" disabled={busy || !asClassCode(code) || !name.trim()}>
        {busy ? "…" : t("classJoin")}
      </button>
    </form>
  );
}
